import axios from "axios";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Form } from "antd";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Swal from "sweetalert2";

interface IProps {
  isRegister: boolean;
  setIsRegister: () => void;
  setIsShowLogin: () => void;
}

const schema = yup.object().shape({
  username: yup.string().required("Tên tài khoản bắt buộc"),
  managedBy: yup.string().required("Đại lý bắt buộc"),
  phone_number: yup
    .string()
    .matches(/^\d{10}$/, "Số điện thoại phải có 10 chữ số")
    .required("Số điện thoại bắt buộc"),
  password: yup.string().min(6, "Mật khẩu ít nhất 6 ký tự").required(),
});

const Register: React.FC<IProps> = ({
  isRegister,
  setIsRegister,
  setIsShowLogin,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const handleRegister = async (data: any) => {
      try {
        await axios
          .post(`${process.env.REACT_APP_URL_API}/auth/register`, {
            username: data.username,
            managedByUsername: data.managedBy,
            phone: data.phone_number,
            password: String(data.password),
          })
          .then((data) => {
            if (data.status === 201) {
              Swal.fire({
                icon: "success",
                title: "Đăng ký thành công",
                text: "Vui lòng đợi ít phút. ",
                customClass: {
                  popup: "custom-swal",
                  title: "custom-title",
                  htmlContainer: "custom-text",
                },
              });
              setIsRegister();
              setIsShowLogin();
              reset();
            }
          })
          .catch((err) => {
            if (
              err.response.data.message[0] === "managedBy must be a mongodb id"
            ) {
              Swal.fire({
                icon: "error",
                title: "Lỗi đăng ký",
                text: "Thông tin đại lý chưa chính xác!",
                customClass: {
                  popup: "custom-swal",
                  title: "custom-title",
                  htmlContainer: "custom-text",
                },
              });
            } else {
              Swal.fire({
                icon: "error",
                title: "Lỗi đăng ký",
                text: `${err.response.data.message}`,
                customClass: {
                  popup: "custom-swal",
                  title: "custom-title",
                  htmlContainer: "custom-text",
                },
              });
            }
          });
      } catch (error) {
        return error;
      }
  };

  return (
    <div
      className="register-overlay"
      style={{
        position: "fixed",
        inset: 0,
        display: isRegister ? "flex" : "none",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        background: "transparent",
        backdropFilter: "none",
      }}
    >
      <div className="velixia-logo-wrap velixia-logo-wrap--outside">
        <img
          src="/assets/logo.png"
          alt="Velixia"
          className="velixia-logo-img"
          width={173}
          height={170}
          onError={(ev) => {
            (ev.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      </div>
      <div className="auth-popup-velixia auth-popup-velixia--register register-modal-box">
        <h1 className="velixia-title">VELIXIA SLOT</h1>
        <p className="velixia-subtitle">AI SLOT ANALYSIS SYSTEM</p>

        <Form id="regisform" onFinish={handleSubmit(handleRegister)}>
          <div style={{ marginBottom: 4 }}>
            <Form.Item
              validateStatus={errors.username ? "error" : ""}
              help={errors.username?.message}
              style={{ marginBottom: 0 }}
            >
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <div className="velixia-field">
                    <span className="velixia-field-icon" aria-hidden>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </span>
                    <input
                      {...field}
                      className="velixia-input"
                      placeholder="Tài khoản"
                      maxLength={16}
                      autoComplete="username"
                    />
                  </div>
                )}
              />
            </Form.Item>
          </div>

          <div style={{ marginBottom: 4 }}>
            <Form.Item
              validateStatus={errors.phone_number ? "error" : ""}
              help={errors.phone_number?.message}
              style={{ marginBottom: 0 }}
            >
              <Controller
                name="phone_number"
                control={control}
                render={({ field }) => (
                  <div className="velixia-field">
                    <span className="velixia-field-icon" aria-hidden>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </span>
                    <input
                      {...field}
                      className="velixia-input"
                      placeholder="Số điện thoại"
                      maxLength={10}
                    />
                  </div>
                )}
              />
            </Form.Item>
          </div>

          <div style={{ marginBottom: 4 }}>
            <Form.Item
              validateStatus={errors.password ? "error" : ""}
              help={errors.password?.message}
              style={{ marginBottom: 0 }}
            >
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <div className="velixia-field">
                    <span className="velixia-field-icon" aria-hidden>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </span>
                    <input
                      {...field}
                      className="velixia-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="Mật khẩu"
                      maxLength={16}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="velixia-field-eye"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        {showPassword ? (
                          <>
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </>
                        ) : (
                          <>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                )}
              />
            </Form.Item>
          </div>

          <div style={{ marginBottom: 4 }}>
            <Form.Item
              validateStatus={errors.managedBy ? "error" : ""}
              help={errors.managedBy?.message}
              style={{ marginBottom: 0 }}
            >
              <Controller
                name="managedBy"
                control={control}
                render={({ field }) => (
                  <div className="velixia-field">
                    <span className="velixia-field-icon" aria-hidden>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </span>
                    <input {...field} className="velixia-input" placeholder="Mã giới thiệu" />
                  </div>
                )}
              />
            </Form.Item>
          </div>

          <button type="submit" className="velixia-btn">
            Đăng ký ngay
          </button>
        </Form>

        <div className="velixia-footer">
          <span className="velixia-footer-muted">Đã có tài khoản? </span>
          <span
            role="button"
            tabIndex={0}
            className="velixia-footer-link"
            onClick={() => {
              setIsRegister();
              setIsShowLogin();
            }}
            onKeyDown={(e) => e.key === "Enter" && (setIsRegister(), setIsShowLogin())}
          >
            Đăng nhập ngay
          </span>
        </div>
      </div>
    </div>
  );
};

export default Register;
